import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import usePostData from "@/hooks/usePostData";
import { useFetchDataV2 } from "@/hooks/useFetchDataV2";
import { toast } from "@/components/ui/use-toast";
import { useQueryClient } from "@tanstack/react-query";
import {
  Copy,
  EllipsisVertical,
  FilePlus2,
  FolderKanban,
  Layers3,
  Loader2,
  MessageCircleWarning,
  Sparkles,
  Trash,
} from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import DefaultFormJson from "../../DefaultForm.json";

const API_URL = import.meta.env.VITE_API_ENDPOINT;
const FORMS_LIBRARY_CACHE_ID = "parameters-forms-library";
const FORMS_LIBRARY_CACHE_TTL_MS = 1000 * 60 * 60;
const PROJECT_TYPES_CACHE_ID = "project-types-parameters";
const PROJECT_TYPES_CACHE_TTL_MS = 1000 * 60 * 60;

type LibraryItem = {
  scope: "project_type" | "call";
  projectTypeId?: number;
  callId?: number;
  preservedCallId?: number | null;
  programId?: number | null;
  name: string;
  canDelete?: boolean;
  usageCount?: number;
  sourceDeleted?: boolean;
  preservedDeletedAt?: string | null;
};

type ProjectTypeChoice = {
  id: number;
  name: string;
  type: number;
  parentId: number | null;
  parentName: string;
};

type LibraryData = {
  projectTypeForms?: LibraryItem[];
  callForms?: LibraryItem[];
  [key: string]: unknown;
};

type CacheSnapshot = Array<{
  queryKey: readonly unknown[];
  data: unknown;
}>;

type SourceField = {
  name: string;
  label: string;
  type: string;
  required?: boolean;
  options?: unknown;
  nativeField?: string | null;
  step?: number;
};

type SourceSection = {
  name: string;
  label: string;
  description?: string | null;
  step?: number;
  fields?: SourceField[];
};

type SourceStep = {
  id?: number;
  name: string;
  label: string;
  description?: string | null;
  step?: number;
  options?: Record<string, unknown> | null;
  fields?: SourceField[];
  sections?: SourceSection[];
};

type DefaultFormSection = SourceSection & {
  nameSection?: string;
};

type DefaultFormStep = {
  nameStep: string;
  description?: string | null;
  fields?: SourceField[];
  sections?: DefaultFormSection[];
};

type ProjectTypesResponse =
  | ProjectTypeChoice[]
  | {
      "hydra:member"?: unknown[];
    };

const asRecord = (value: unknown): Record<string, unknown> =>
  value && typeof value === "object" && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {};

const CALL_MODEL_OPTION_KEYS = new Set([
  "callFormSourceScope",
  "callFormSourceProjectTypeId",
  "preservedCallId",
  "preservedCallName",
  "preservedFromDeletedCall",
  "preservedDeletedAt",
]);

const INTRO_STEP_API_NAME = "introduction";

const toApiStepName = (label: string, fallbackStep: number): string => {
  const trimmed = label.trim();
  if (!trimmed) {
    return `step_${fallbackStep}`;
  }

  const cleaned = trimmed
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");

  return cleaned || `step_${fallbackStep}`;
};

const buildDefaultInitializePayload = () => ({
  steps: (DefaultFormJson as DefaultFormStep[]).map((item, index) => {
    const apiName = toApiStepName(item.nameStep ?? "", index + 1);
    const isIntro = apiName === INTRO_STEP_API_NAME;

    return {
      step: index + 1,
      name: isIntro ? INTRO_STEP_API_NAME : apiName,
      label: item.nameStep,
      description: item.description ?? null,
      type: isIntro ? "intro" : "step",
      options: isIntro ? { description: item.description ?? "" } : undefined,
      fields: Array.isArray(item.fields)
        ? item.fields.map((field, fieldIndex) => ({
            name: field.name,
            label: field.label,
            type: field.type,
            required: field.required,
            options: field.options ?? null,
            nativeField: field.nativeField,
            step: fieldIndex + 1,
          }))
        : [],
      sections: Array.isArray(item.sections)
        ? item.sections.map((section, sectionIndex) => ({
            nameSection:
              section.name ??
              section.nameSection ??
              toApiStepName(section.label ?? `section_${sectionIndex + 1}`, sectionIndex + 1),
            label: section.label,
            description: section.description ?? null,
            step: section.step ?? sectionIndex + 1,
            fields: Array.isArray(section.fields)
              ? section.fields.map((field, fieldIndex) => ({
                  name: field.name,
                  label: field.label,
                  type: field.type,
                  required: field.required,
                  options: field.options ?? null,
                  nativeField: field.nativeField,
                  step: field.step ?? fieldIndex + 1,
                }))
              : [],
          }))
        : [],
    };
  }),
});

const sanitizeStepOptionsForTypeDuplicate = (options: unknown) => {
  if (!options || typeof options !== "object" || Array.isArray(options)) {
    return options ?? null;
  }

  const nextOptions = { ...(options as Record<string, unknown>) };
  CALL_MODEL_OPTION_KEYS.forEach((key) => {
    delete nextOptions[key];
  });

  return Object.keys(nextOptions).length > 0 ? nextOptions : null;
};

const buildInitializePayloadFromSchema = (schema: SourceStep[]) => ({
  steps: schema.map((step, index) => ({
    step: Number(step.step ?? index + 1),
    name: step.name,
    label: step.label,
    description: step.description ?? null,
    options: sanitizeStepOptionsForTypeDuplicate(step.options),
    type: step.name === "introduction" ? "intro" : "step",
    fields: Array.isArray(step.fields)
      ? step.fields.map((field, fieldIndex) => ({
          name: field.name,
          label: field.label,
          type: field.type,
          required: Boolean(field.required),
          options: field.options ?? null,
          nativeField: field.nativeField ?? null,
          step: Number(field.step ?? fieldIndex + 1),
        }))
      : [],
    sections: Array.isArray(step.sections)
      ? step.sections.map((section, sectionIndex) => ({
          nameSection: section.name,
          label: section.label,
          description: section.description ?? null,
          step: Number(section.step ?? sectionIndex + 1),
          fields: Array.isArray(section.fields)
            ? section.fields.map((field, fieldIndex) => ({
                name: field.name,
                label: field.label,
                type: field.type,
                required: Boolean(field.required),
                options: field.options ?? null,
                nativeField: field.nativeField ?? null,
                step: Number(field.step ?? fieldIndex + 1),
              }))
            : [],
        }))
      : [],
  })),
});

const ParametersFormsLibrary = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [openCreateDialog, setOpenCreateDialog] = useState(false);
  const libraryEndpoint = `${API_URL}/field-forms/library`;
  const library = useFetchDataV2<LibraryData>(libraryEndpoint, {
    cacheId: FORMS_LIBRARY_CACHE_ID,
    staleTime: FORMS_LIBRARY_CACHE_TTL_MS,
    gcTime: FORMS_LIBRARY_CACHE_TTL_MS,
    browserCache: true,
    browserCacheKey: FORMS_LIBRARY_CACHE_ID,
    browserCacheTtlMs: FORMS_LIBRARY_CACHE_TTL_MS,
  });
  const projectTypes = useFetchDataV2<ProjectTypesResponse>(`${API_URL}/project-types`, {
    cacheId: PROJECT_TYPES_CACHE_ID,
    staleTime: PROJECT_TYPES_CACHE_TTL_MS,
    gcTime: PROJECT_TYPES_CACHE_TTL_MS,
    browserCache: true,
    browserCacheKey: PROJECT_TYPES_CACHE_ID,
    browserCacheTtlMs: PROJECT_TYPES_CACHE_TTL_MS,
  });
  const [activeTypeTab, setActiveTypeTab] = useState<1 | 2>(1);
  const [selectedProjectTypeId, setSelectedProjectTypeId] = useState<number | null>(null);
  const [pendingDeleteItem, setPendingDeleteItem] = useState<LibraryItem | null>(null);
  const [blockedDeleteItem, setBlockedDeleteItem] = useState<LibraryItem | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [checkingDeleteKey, setCheckingDeleteKey] = useState<string | null>(null);
  const [duplicateSourceItem, setDuplicateSourceItem] = useState<LibraryItem | null>(null);
  const [duplicateTargetTypeId, setDuplicateTargetTypeId] = useState<number | null>(null);
  const [duplicateTypeTab, setDuplicateTypeTab] = useState<1 | 2>(1);
  const [isDuplicating, setIsDuplicating] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const { fetchData } = useFetchDataV2("");
  const { postData } = usePostData();

  const isLibraryQueryKey = (queryKey: readonly unknown[]) =>
    Array.isArray(queryKey) &&
    typeof queryKey[1] === "string" &&
    queryKey[1] === libraryEndpoint;

  const getLibrarySnapshots = (): CacheSnapshot =>
    queryClient
      .getQueryCache()
      .findAll({ predicate: (query) => isLibraryQueryKey(query.queryKey) })
      .map((query) => ({
        queryKey: query.queryKey,
        data: queryClient.getQueryData(query.queryKey),
      }));

  const restoreLibrarySnapshots = (snapshots: CacheSnapshot) => {
    snapshots.forEach((snapshot) => {
      queryClient.setQueryData(snapshot.queryKey, snapshot.data);
    });
  };

  const isSameLibraryItem = (left: LibraryItem, right: LibraryItem) => {
    if (left.scope !== right.scope) return false;

    if (left.scope === "project_type") {
      return Number(left.projectTypeId ?? 0) === Number(right.projectTypeId ?? 0);
    }

    return (
      Number(left.callId ?? 0) === Number(right.callId ?? 0) &&
      Number(left.preservedCallId ?? 0) === Number(right.preservedCallId ?? 0)
    );
  };

  const updateLibraryCache = (updater: (previous: LibraryData) => LibraryData) => {
    queryClient.setQueriesData(
      { predicate: (query) => isLibraryQueryKey(query.queryKey) },
      (previous: unknown) => {
        if (!previous || typeof previous !== "object" || Array.isArray(previous)) {
          return previous;
        }

        return updater(previous as LibraryData);
      },
    );
  };

  const addOptimisticProjectTypeForm = (projectType: ProjectTypeChoice) => {
    const optimisticItem: LibraryItem = {
      scope: "project_type",
      projectTypeId: projectType.id,
      name: projectType.name,
      canDelete: true,
      usageCount: 0,
    };

    updateLibraryCache((previous) => {
      const projectTypeForms = Array.isArray(previous.projectTypeForms)
        ? previous.projectTypeForms
        : [];
      const alreadyExists = projectTypeForms.some((item) =>
        isSameLibraryItem(item, optimisticItem),
      );

      return {
        ...previous,
        projectTypeForms: alreadyExists
          ? projectTypeForms
          : [optimisticItem, ...projectTypeForms],
      };
    });

    return optimisticItem;
  };

  const removeOptimisticForm = (itemToRemove: LibraryItem) => {
    updateLibraryCache((previous) => ({
      ...previous,
      projectTypeForms: Array.isArray(previous.projectTypeForms)
        ? previous.projectTypeForms.filter((item) => !isSameLibraryItem(item, itemToRemove))
        : previous.projectTypeForms,
      callForms: Array.isArray(previous.callForms)
        ? previous.callForms.filter((item) => !isSameLibraryItem(item, itemToRemove))
        : previous.callForms,
    }));
  };

  const typeForms = useMemo<LibraryItem[]>(
    () =>
      Array.isArray(library.data?.projectTypeForms)
        ? library.data.projectTypeForms
        : [],
    [library.data],
  );
  const callForms = useMemo<LibraryItem[]>(
    () => (Array.isArray(library.data?.callForms) ? library.data.callForms : []),
    [library.data],
  );

  const allProjectTypes = useMemo(() => {
    const raw = Array.isArray(projectTypes.data?.["hydra:member"])
      ? projectTypes.data["hydra:member"]
      : Array.isArray(projectTypes.data)
        ? projectTypes.data
        : [];

    return raw
      .map((item) => {
        const source = asRecord(item);
        const parent = asRecord(source.parent);

        return {
          id: Number(source.id),
          name: String(source.name ?? "").trim(),
          type: Number(source.type ?? 1),
          parentId: Number(parent.id ?? 0) || null,
          parentName: String(parent.name ?? "").trim(),
        };
      })
      .filter(
        (item): item is ProjectTypeChoice =>
          Number.isFinite(item.id) &&
          item.id > 0 &&
          item.name !== "" &&
          (item.type === 1 || item.type === 2),
      );
  }, [projectTypes.data]);

  const usedProjectTypeIds = useMemo(
    () => new Set(typeForms.map((item) => Number(item.projectTypeId)).filter((id) => id > 0)),
    [typeForms],
  );

  const availableProjectTypes = useMemo(
    () => allProjectTypes.filter((item) => !usedProjectTypeIds.has(item.id)),
    [allProjectTypes, usedProjectTypeIds],
  );

  const availableProjectTypesByTab = useMemo(
    () => availableProjectTypes.filter((item) => item.type === activeTypeTab),
    [availableProjectTypes, activeTypeTab],
  );

  const availableGroups = useMemo(() => {
    const groups = new Map<
      string,
      { key: string; label: string; items: ProjectTypeChoice[] }
    >();

    availableProjectTypesByTab.forEach((item) => {
      const label = item.parentName || "Sans sous-type";
      const key = item.parentId ? `parent-${item.parentId}` : `parent-none-${label}`;
      if (!groups.has(key)) {
        groups.set(key, { key, label, items: [] });
      }
      groups.get(key)?.items.push(item);
    });

    return Array.from(groups.values()).sort((a, b) =>
      a.label.localeCompare(b.label, "fr", { sensitivity: "base" }),
    );
  }, [availableProjectTypesByTab]);

  const selectedProjectType = useMemo(
    () =>
      availableProjectTypes.find((item) => item.id === selectedProjectTypeId) ?? null,
    [availableProjectTypes, selectedProjectTypeId],
  );
  const selectedDuplicateTargetType = useMemo(
    () =>
      availableProjectTypes.find((item) => item.id === duplicateTargetTypeId) ?? null,
    [availableProjectTypes, duplicateTargetTypeId],
  );
  const availableDuplicateProjectTypesByTab = useMemo(
    () => availableProjectTypes.filter((item) => item.type === duplicateTypeTab),
    [availableProjectTypes, duplicateTypeTab],
  );
  const availableDuplicateGroups = useMemo(() => {
    const groups = new Map<
      string,
      { key: string; label: string; items: ProjectTypeChoice[] }
    >();

    availableDuplicateProjectTypesByTab.forEach((item) => {
      const label = item.parentName || "Sans sous-type";
      const key = item.parentId ? `parent-${item.parentId}` : `parent-none-${label}`;
      if (!groups.has(key)) {
        groups.set(key, { key, label, items: [] });
      }
      groups.get(key)?.items.push(item);
    });

    return Array.from(groups.values()).sort((a, b) =>
      a.label.localeCompare(b.label, "fr", { sensitivity: "base" }),
    );
  }, [availableDuplicateProjectTypesByTab]);

  useEffect(() => {
    if (!openCreateDialog) {
      setSelectedProjectTypeId(null);
      setActiveTypeTab(1);
    }
  }, [openCreateDialog]);

  useEffect(() => {
    if (!duplicateSourceItem) {
      setDuplicateTargetTypeId(null);
      setDuplicateTypeTab(1);
    }
  }, [duplicateSourceItem]);

  useEffect(() => {
    if (
      selectedProjectTypeId &&
      !availableProjectTypesByTab.some((item) => item.id === selectedProjectTypeId)
    ) {
      setSelectedProjectTypeId(null);
    }
  }, [activeTypeTab, availableProjectTypesByTab, selectedProjectTypeId]);

  useEffect(() => {
    if (
      duplicateTargetTypeId &&
      !availableDuplicateProjectTypesByTab.some((item) => item.id === duplicateTargetTypeId)
    ) {
      setDuplicateTargetTypeId(null);
    }
  }, [availableDuplicateProjectTypesByTab, duplicateTargetTypeId, duplicateTypeTab]);

  const openTypeForm = (item: LibraryItem) => {
    if (!item.projectTypeId) return;
    navigate(`/dashboard/parameters?params=2&projectTypeId=${item.projectTypeId}`);
  };

  const openCallForm = (item: LibraryItem) => {
    const params = new URLSearchParams();
    params.set("params", "2");
    if (item.callId) {
      params.set("callId", `${item.callId}`);
    } else if (item.preservedCallId) {
      params.set("preservedCallId", `${item.preservedCallId}`);
    } else {
      return;
    }
    if (item.programId) params.set("programId", `${item.programId}`);
    if (item.name) params.set("programName", item.name);
    navigate(`/dashboard/parameters?${params.toString()}`);
  };

  const handleDeleteClick = async (item: LibraryItem) => {
    const deleteKey =
      item.scope === "project_type"
        ? `project_type-${item.projectTypeId ?? 0}`
        : `call-${item.callId ?? item.preservedCallId ?? 0}`;
    setCheckingDeleteKey(deleteKey);
    let resolvedItem: LibraryItem = item;

    try {
      const query = new URLSearchParams();
      if (item.scope === "project_type" && item.projectTypeId) {
        query.set("projectTypeId", String(item.projectTypeId));
      } else if (item.scope === "call" && item.callId) {
        query.set("callId", String(item.callId));
      } else if (item.scope === "call" && item.preservedCallId) {
        query.set("preservedCallId", String(item.preservedCallId));
      }
      const usage = await fetchData(`${API_URL}/field-forms/usage?${query.toString()}`);
      resolvedItem = {
        ...item,
        usageCount: Number(usage?.usageCount ?? item.usageCount ?? 0),
        canDelete:
          typeof usage?.canDelete === "boolean"
            ? usage.canDelete
            : item.canDelete,
      };
    } catch (_error) {
      resolvedItem = item;
    } finally {
      setCheckingDeleteKey(null);
    }

    if ((resolvedItem.usageCount ?? 0) > 0 || resolvedItem.canDelete === false) {
      setBlockedDeleteItem(resolvedItem);
      return;
    }
    setPendingDeleteItem(resolvedItem);
  };

  const handleConfirmDelete = async () => {
    if (!pendingDeleteItem) return;
    const itemToDelete = pendingDeleteItem;
    const snapshots = getLibrarySnapshots();

    setIsDeleting(true);
    setPendingDeleteItem(null);
    removeOptimisticForm(itemToDelete);

    try {
      const query = new URLSearchParams();
      if (itemToDelete.scope === "project_type" && itemToDelete.projectTypeId) {
        query.set("projectTypeId", String(itemToDelete.projectTypeId));
      }
      if (itemToDelete.scope === "call" && itemToDelete.callId) {
        query.set("callId", String(itemToDelete.callId));
      }
      if (itemToDelete.scope === "call" && itemToDelete.preservedCallId) {
        query.set("preservedCallId", String(itemToDelete.preservedCallId));
      }
      const result = await postData({
        url: `${API_URL}/field_forms/delete-model?${query.toString()}`,
        data: {},
      });

      if (!result) {
        throw new Error("Suppression du formulaire impossible.");
      }
    } catch (_error) {
      restoreLibrarySnapshots(snapshots);
      toast({
        title: "Suppression du formulaire impossible",
        variant: "destructive",
      });
    } finally {
      setIsDeleting(false);
    }
  };

  const handleOpenDuplicate = (item: LibraryItem) => {
    setDuplicateSourceItem(item);
  };

  const handleConfirmDuplicate = async () => {
    if (!duplicateSourceItem || !duplicateTargetTypeId) return;
    const sourceItem = duplicateSourceItem;
    const targetType = selectedDuplicateTargetType;
    if (!targetType) return;
    const snapshots = getLibrarySnapshots();

    setIsDuplicating(true);
    setDuplicateSourceItem(null);
    addOptimisticProjectTypeForm(targetType);

    try {
      const query = new URLSearchParams();
      if (sourceItem.scope === "project_type" && sourceItem.projectTypeId) {
        query.set("projectTypeId", String(sourceItem.projectTypeId));
      }
      if (sourceItem.scope === "call" && sourceItem.callId) {
        query.set("callId", String(sourceItem.callId));
      }
      if (sourceItem.scope === "call" && sourceItem.preservedCallId) {
        query.set("preservedCallId", String(sourceItem.preservedCallId));
      }

      const sourceSchema = await fetchData(`${API_URL}/field-forms/global?${query.toString()}`);
      if (!Array.isArray(sourceSchema) || sourceSchema.length === 0) {
        throw new Error("Modèle source introuvable.");
      }

      const payload = buildInitializePayloadFromSchema(sourceSchema as SourceStep[]);
      const initializeResult = await postData({
        url: `${API_URL}/field_forms/initialize?projectTypeId=${duplicateTargetTypeId}`,
        data: payload,
      });

      if (!initializeResult || initializeResult.error) {
        throw new Error("Duplication du formulaire impossible.");
      }

      navigate(`/dashboard/parameters?params=2&projectTypeId=${duplicateTargetTypeId}`);
    } catch (_error) {
      restoreLibrarySnapshots(snapshots);
      setDuplicateSourceItem(sourceItem);
      toast({
        title: "Duplication du formulaire impossible",
        variant: "destructive",
      });
    } finally {
      setIsDuplicating(false);
    }
  };

  const handleCreateForm = async () => {
    if (!selectedProjectTypeId || !selectedProjectType) return;
    const snapshots = getLibrarySnapshots();
    const optimisticItem = addOptimisticProjectTypeForm(selectedProjectType);

    setIsCreating(true);
    setOpenCreateDialog(false);

    try {
      const initializeResult = await postData({
        url: `${API_URL}/field_forms/initialize?projectTypeId=${selectedProjectTypeId}`,
        data: buildDefaultInitializePayload(),
      });

      if (!initializeResult || initializeResult.error) {
        throw new Error("Création du formulaire impossible.");
      }

      await queryClient.invalidateQueries();
      navigate(`/dashboard/parameters?params=2&projectTypeId=${selectedProjectTypeId}`);
    } catch {
      restoreLibrarySnapshots(snapshots);
      removeOptimisticForm(optimisticItem);
      toast({
        title: "Création du formulaire impossible",
        variant: "destructive",
      });
    } finally {
      setIsCreating(false);
    }
  };

  const renderEmptyState = (message: string) => (
    <div className="col-span-full rounded-3xl border border-dashed border-slate-300 bg-white p-6 text-sm text-slate-500">
      {message}
    </div>
  );

  return (
    <div className="space-y-10">
      <div className="relative overflow-hidden rounded-[28px] border border-[#d7e5d4] bg-[linear-gradient(135deg,#f7fbf6_0%,#eef6ec_40%,#ffffff_100%)] p-8 shadow-[0_20px_50px_rgba(29,82,20,0.12)]">
        <div className="absolute -right-10 -top-10 h-40 w-40 rounded-full bg-[#d8e8d4]/70 blur-3xl" />
        <div className="absolute bottom-0 left-0 h-24 w-24 rounded-full bg-[#edf5eb]/80 blur-2xl" />
        <div className="relative flex flex-wrap items-start justify-between gap-4">
          <div className="max-w-2xl">
            <div className="mb-3 inline-flex items-center gap-2 rounded-full border border-[#d3e2cf] bg-white/90 px-3 py-1 text-xs font-semibold uppercase tracking-[0.18em] text-[#1d5214]">
              <Sparkles className="h-3.5 w-3.5" />
              Mes formulaires
            </div>
            <h1 className="text-3xl font-semibold tracking-tight text-[#1d5214]">
              Gérez vos formulaires personnalisés par type de projet ou par appel à projets.
            </h1>
            <p className="mt-3 max-w-xl text-sm leading-6 text-[#4c6f46]">
              Chaque formulaire correspond à un périmètre dédié. Les formulaires par type de projet servent de base à vos projets personnalisés, tandis que les formulaires d'appel à projets restent liés à un programme précis.
            </p>
          </div>
          <Button
            type="button"
            className="rounded-full bg-[#1d5214] px-5 py-6 text-sm font-semibold text-white hover:bg-[#174010]"
            onClick={() => setOpenCreateDialog(true)}
          >
            <FilePlus2 className="mr-2 h-4 w-4" />
            Ajouter un formulaire
          </Button>
        </div>
      </div>

      <section className="space-y-5">
        <div className="flex items-center gap-3">
          <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-emerald-100 text-emerald-700">
            <Layers3 className="h-5 w-5" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-slate-900">Formulaires par type de projet</h2>
            <p className="text-sm text-slate-500">
              {typeForms.length} formulaire{typeForms.length > 1 ? "s" : ""} créé{typeForms.length > 1 ? "s" : ""}
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-5">
          {typeForms.length === 0
            ? renderEmptyState("Aucun formulaire par type de projet n'a encore été créé.")
            : typeForms.map((item) => (
                <div
                  key={`type-${item.projectTypeId}`}
                  className="group flex min-h-[132px] flex-col justify-start rounded-2xl border border-slate-200 bg-white p-4 text-left shadow-sm transition-all hover:-translate-y-0.5 hover:border-emerald-300 hover:shadow-[0_18px_35px_rgba(15,118,110,0.12)]"
                >
                  <div className="flex items-start justify-between gap-2">
                    <button
                      type="button"
                      onClick={() => openTypeForm(item)}
                      className="flex-1 text-left"
                    >
                      <p className="text-xs font-semibold uppercase tracking-[0.16em] text-emerald-600">
                        Type de projet
                      </p>
                      <h3 className="mt-3 break-words text-base font-semibold leading-6 text-slate-900">
                        {item.name}
                      </h3>
                    </button>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <button
                          type="button"
                          disabled={checkingDeleteKey === `project_type-${item.projectTypeId ?? 0}`}
                          className="rounded-md p-2 text-slate-500 transition-colors hover:bg-slate-100 hover:text-slate-700"
                          aria-label="Actions du formulaire"
                        >
                          {checkingDeleteKey === `project_type-${item.projectTypeId ?? 0}` ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            <EllipsisVertical className="h-4 w-4" />
                          )}
                        </button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem
                          onClick={() => handleOpenDuplicate(item)}
                          className="cursor-pointer focus:bg-slate-900 focus:text-white"
                        >
                          <Copy className="mr-2 h-4 w-4" />
                          Dupliquer
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onClick={() => void handleDeleteClick(item)}
                          disabled={checkingDeleteKey === `project_type-${item.projectTypeId ?? 0}`}
                          className="cursor-pointer text-red-600 focus:bg-red-600 focus:text-white"
                        >
                          {checkingDeleteKey === `project_type-${item.projectTypeId ?? 0}` ? (
                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          ) : (
                            <Trash className="mr-2 h-4 w-4" />
                          )}
                          Supprimer
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </div>
              ))}
        </div>
      </section>

      <section className="space-y-5">
        <div className="flex items-center gap-3">
          <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-lime-100 text-lime-700">
            <FolderKanban className="h-5 w-5" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-slate-900">Formulaires d'appel à projets</h2>
            <p className="text-sm text-slate-500">
              {callForms.length} formulaire{callForms.length > 1 ? "s" : ""} spécifique{callForms.length > 1 ? "s" : ""}
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-5">
          {callForms.length === 0
            ? renderEmptyState("Aucun formulaire spécifique à un appel à projets n'est disponible.")
            : callForms.map((item) => (
                <div
                  key={`call-${item.callId ?? item.preservedCallId ?? item.name}`}
                  className={`group flex min-h-[132px] flex-col justify-start rounded-2xl border bg-white p-4 text-left shadow-sm transition-all hover:-translate-y-0.5 ${
                    item.sourceDeleted
                      ? "border-red-200 hover:border-red-300 hover:shadow-[0_18px_35px_rgba(220,38,38,0.10)]"
                      : "border-slate-200 hover:border-lime-300 hover:shadow-[0_18px_35px_rgba(132,204,22,0.12)]"
                  }`}
                >
                  <div className="flex items-start justify-between gap-2">
                    <button
                      type="button"
                      onClick={() => openCallForm(item)}
                      className="flex-1 text-left"
                    >
                      <div className="flex h-4 items-center gap-1.5 leading-none">
                        <span
                          className={`inline-flex items-center text-xs font-semibold uppercase leading-none tracking-[0.16em] ${
                            item.sourceDeleted ? "text-red-600" : "text-lime-600"
                          }`}
                        >
                          Appel à projets
                        </span>
                        {item.sourceDeleted ? (
                          <TooltipProvider delayDuration={150}>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <span className="inline-flex h-4 w-4 shrink-0 items-center justify-center self-center text-red-600">
                                  <MessageCircleWarning className="h-3.5 w-3.5" />
                                </span>
                              </TooltipTrigger>
                              <TooltipContent className="border-red-700 bg-red-600 text-xs text-white">
                                L&apos;appel à projets est déjà supprimé.
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        ) : null}
                      </div>
                      <h3 className="mt-3 break-words text-base font-semibold leading-6 text-slate-900">
                        {item.name}
                      </h3>
                    </button>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <button
                          type="button"
                          disabled={checkingDeleteKey === `call-${item.callId ?? item.preservedCallId ?? 0}`}
                          className="mt-[-2px] rounded-md p-2 text-slate-500 transition-colors hover:bg-slate-100 hover:text-slate-700"
                          aria-label="Actions du formulaire"
                        >
                          {checkingDeleteKey === `call-${item.callId ?? item.preservedCallId ?? 0}` ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            <EllipsisVertical className="h-4 w-4" />
                          )}
                        </button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem
                          onClick={() => handleOpenDuplicate(item)}
                          className="cursor-pointer focus:bg-slate-900 focus:text-white"
                        >
                          <Copy className="mr-2 h-4 w-4" />
                          Dupliquer
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onClick={() => void handleDeleteClick(item)}
                          disabled={checkingDeleteKey === `call-${item.callId ?? item.preservedCallId ?? 0}`}
                          className="cursor-pointer text-red-600 focus:bg-red-600 focus:text-white"
                        >
                          {checkingDeleteKey === `call-${item.callId ?? item.preservedCallId ?? 0}` ? (
                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          ) : (
                            <Trash className="mr-2 h-4 w-4" />
                          )}
                          Supprimer
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </div>
              ))}
        </div>
      </section>

      <Dialog open={openCreateDialog} onOpenChange={setOpenCreateDialog}>
        <DialogContent className="sm:max-w-[680px]">
          <DialogHeader>
            <DialogTitle>Ajouter un formulaire</DialogTitle>
            <DialogDescription>
              Sélectionnez un type de projet qui ne dispose pas encore de formulaire personnalisé.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                onClick={() => setActiveTypeTab(1)}
                className={`relative rounded-[10px] px-[15px] py-[10px] text-sm font-semibold ${
                  activeTypeTab === 1
                    ? "bg-[#f2f7ec] text-verdeoorange"
                    : "bg-[rgba(194,194,194,0.1)] text-[#2D3720]"
                }`}
              >
                Type environnemental
                {activeTypeTab === 1 && (
                  <span className="absolute bottom-0 left-[3px] h-[3px] w-[calc(100%-6px)] rounded-[2px] bg-verdeoorange" />
                )}
              </button>
              <button
                type="button"
                onClick={() => setActiveTypeTab(2)}
                className={`relative rounded-[10px] px-[15px] py-[10px] text-sm font-semibold ${
                  activeTypeTab === 2
                    ? "bg-[#fff5ea] text-verdeoorange"
                    : "bg-[rgba(194,194,194,0.1)] text-[#2D3720]"
                }`}
              >
                Type social
                {activeTypeTab === 2 && (
                  <span className="absolute bottom-0 left-[3px] h-[3px] w-[calc(100%-6px)] rounded-[2px] bg-verdeoorange" />
                )}
              </button>
            </div>

            <div
              className={`rounded-[20px] p-4 ${
                activeTypeTab === 1 ? "bg-[#f2f7ec]" : "bg-[#fff5ea]"
              }`}
            >
            {projectTypes.loading ? (
              <div className="flex items-center gap-3 rounded-2xl border border-slate-200 bg-slate-50 p-4 text-sm text-slate-500">
                <Loader2 className="h-4 w-4 animate-spin" />
                Chargement des types de projet...
              </div>
            ) : availableProjectTypes.length === 0 ? (
              <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4 text-sm text-slate-500">
                Tous les types de projet disposent déjà d'un formulaire.
              </div>
            ) : availableGroups.length === 0 ? (
              <div className="rounded-2xl border border-slate-200 bg-white p-4 text-sm text-slate-500">
                Aucun type de projet n'est disponible dans cet onglet.
              </div>
            ) : (
              <div className="max-h-[52vh] overflow-auto pr-1">
                <Accordion
                  type="multiple"
                  defaultValue={availableGroups.slice(0, 2).map((group) => group.key)}
                  className="space-y-3"
                >
                  {availableGroups.map((group) => (
                    <AccordionItem
                      key={group.key}
                      value={group.key}
                      className="overflow-hidden rounded-2xl border border-white/70 bg-white/80 px-4"
                    >
                      <AccordionTrigger className="py-4 text-left text-sm font-semibold text-slate-900 hover:no-underline">
                        <div className="flex items-center gap-3">
                          <span className="inline-flex rounded-full bg-white px-3 py-1 text-xs font-semibold text-slate-500 shadow-sm">
                            {group.items.length}
                          </span>
                          <span>{group.label}</span>
                        </div>
                      </AccordionTrigger>
                      <AccordionContent className="pb-4">
                        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                          {group.items.map((item) => (
                            <button
                              key={item.id}
                              type="button"
                              onClick={() => setSelectedProjectTypeId(item.id)}
                              className={`rounded-2xl border px-4 py-4 text-left transition-all ${
                                selectedProjectTypeId === item.id
                                  ? "border-emerald-400 bg-emerald-50 shadow-[0_10px_25px_rgba(16,185,129,0.12)]"
                                  : "border-slate-200 bg-white hover:border-emerald-300 hover:bg-emerald-50/60"
                              }`}
                            >
                              <div className="flex items-start justify-between gap-3">
                                <div>
                                  <p className="text-sm font-semibold text-slate-900">
                                    {item.name}
                                  </p>
                                  <p className="mt-1 text-xs text-slate-500">
                                    Type de projet
                                  </p>
                                </div>
                                <span
                                  className={`mt-1 inline-flex h-5 w-5 shrink-0 rounded-full border ${
                                    selectedProjectTypeId === item.id
                                      ? "border-emerald-500 bg-emerald-500"
                                      : "border-slate-300 bg-white"
                                  }`}
                                >
                                  {selectedProjectTypeId === item.id && (
                                    <span className="m-auto h-2 w-2 rounded-full bg-white" />
                                  )}
                                </span>
                              </div>
                            </button>
                          ))}
                        </div>
                      </AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              </div>
            )}
            </div>
          </div>
          <DialogFooter>
            <div className="flex w-full items-center justify-between gap-3">
              <p className="text-sm text-slate-500">
                {selectedProjectType
                  ? `Type sélectionné : ${selectedProjectType.name}`
                  : "Veuillez sélectionner un type de projet."}
              </p>
              <div className="flex items-center gap-2">
                <Button type="button" variant="outline" onClick={() => setOpenCreateDialog(false)}>
                  Fermer
                </Button>
                <Button
                  type="button"
                  disabled={!selectedProjectTypeId || isCreating}
                  onClick={() => void handleCreateForm()}
                >
                  {isCreating ? "Création..." : "Créer le formulaire"}
                </Button>
              </div>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={blockedDeleteItem !== null} onOpenChange={(open) => !open && setBlockedDeleteItem(null)}>
        <DialogContent className="sm:max-w-[460px]">
          <DialogHeader>
            <DialogTitle>Suppression impossible</DialogTitle>
            <DialogDescription>
              Ce formulaire est encore utilisé par un ou plusieurs appels à projets ou projets. Veuillez d'abord retirer ou réaffecter les éléments qui en dépendent.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button type="button" onClick={() => setBlockedDeleteItem(null)}>
              Fermer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={pendingDeleteItem !== null} onOpenChange={(open) => !open && setPendingDeleteItem(null)}>
        <DialogContent className="sm:max-w-[460px]">
          <DialogHeader>
            <DialogTitle>Supprimer ce formulaire</DialogTitle>
            <DialogDescription>
              Cette action supprimera définitivement le modèle "{pendingDeleteItem?.name ?? ""}". Confirmez-vous cette suppression ?
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setPendingDeleteItem(null)} disabled={isDeleting}>
              Annuler
            </Button>
            <Button type="button" onClick={() => void handleConfirmDelete()} disabled={isDeleting}>
              {isDeleting ? "Suppression..." : "Supprimer"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <Dialog open={duplicateSourceItem !== null} onOpenChange={(open) => !open && setDuplicateSourceItem(null)}>
        <DialogContent className="sm:max-w-[680px]">
          <DialogHeader>
            <DialogTitle>Dupliquer un formulaire</DialogTitle>
            <DialogDescription>
              Le formulaire dupliqué sera obligatoirement créé sur un type de projet disponible.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-600">
              Formulaire source : <span className="font-semibold text-slate-900">{duplicateSourceItem?.name ?? ""}</span>
            </div>
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                onClick={() => setDuplicateTypeTab(1)}
                className={`relative rounded-[10px] px-[15px] py-[10px] text-sm font-semibold ${
                  duplicateTypeTab === 1
                    ? "bg-[#f2f7ec] text-verdeoorange"
                    : "bg-[rgba(194,194,194,0.1)] text-[#2D3720]"
                }`}
              >
                Type environnemental
                {duplicateTypeTab === 1 && (
                  <span className="absolute bottom-0 left-[3px] h-[3px] w-[calc(100%-6px)] rounded-[2px] bg-verdeoorange" />
                )}
              </button>
              <button
                type="button"
                onClick={() => setDuplicateTypeTab(2)}
                className={`relative rounded-[10px] px-[15px] py-[10px] text-sm font-semibold ${
                  duplicateTypeTab === 2
                    ? "bg-[#fff5ea] text-verdeoorange"
                    : "bg-[rgba(194,194,194,0.1)] text-[#2D3720]"
                }`}
              >
                Type social
                {duplicateTypeTab === 2 && (
                  <span className="absolute bottom-0 left-[3px] h-[3px] w-[calc(100%-6px)] rounded-[2px] bg-verdeoorange" />
                )}
              </button>
            </div>
            <div
              className={`rounded-[20px] p-4 ${
                duplicateTypeTab === 1 ? "bg-[#f2f7ec]" : "bg-[#fff5ea]"
              }`}
            >
              {projectTypes.loading ? (
                <div className="flex items-center gap-3 rounded-2xl border border-slate-200 bg-slate-50 p-4 text-sm text-slate-500">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Chargement des types de projet...
                </div>
              ) : availableProjectTypes.length === 0 ? (
                <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4 text-sm text-slate-500">
                  Tous les types de projet disposent déjà d'un formulaire.
                </div>
              ) : availableDuplicateGroups.length === 0 ? (
                <div className="rounded-2xl border border-slate-200 bg-white p-4 text-sm text-slate-500">
                  Aucun type de projet n'est disponible dans cet onglet.
                </div>
              ) : (
                <div className="max-h-[52vh] overflow-auto pr-1">
                  <Accordion
                    type="multiple"
                    defaultValue={availableDuplicateGroups.slice(0, 2).map((group) => group.key)}
                    className="space-y-3"
                  >
                    {availableDuplicateGroups.map((group) => (
                      <AccordionItem
                        key={group.key}
                        value={group.key}
                        className="overflow-hidden rounded-2xl border border-white/70 bg-white/80 px-4"
                      >
                        <AccordionTrigger className="py-4 text-left text-sm font-semibold text-slate-900 hover:no-underline">
                          <div className="flex items-center gap-3">
                            <span className="inline-flex rounded-full bg-white px-3 py-1 text-xs font-semibold text-slate-500 shadow-sm">
                              {group.items.length}
                            </span>
                            <span>{group.label}</span>
                          </div>
                        </AccordionTrigger>
                        <AccordionContent className="pb-4">
                          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                            {group.items.map((item) => (
                              <button
                                key={item.id}
                                type="button"
                                onClick={() => setDuplicateTargetTypeId(item.id)}
                                className={`rounded-2xl border px-4 py-4 text-left transition-all ${
                                  duplicateTargetTypeId === item.id
                                    ? "border-emerald-400 bg-emerald-50 shadow-[0_10px_25px_rgba(16,185,129,0.12)]"
                                    : "border-slate-200 bg-white hover:border-emerald-300 hover:bg-emerald-50/60"
                                }`}
                              >
                                <div className="flex items-start justify-between gap-3">
                                  <div>
                                    <p className="text-sm font-semibold text-slate-900">
                                      {item.name}
                                    </p>
                                    <p className="mt-1 text-xs text-slate-500">
                                      Type de projet
                                    </p>
                                  </div>
                                  <span
                                    className={`mt-1 inline-flex h-5 w-5 shrink-0 rounded-full border ${
                                      duplicateTargetTypeId === item.id
                                        ? "border-emerald-500 bg-emerald-500"
                                        : "border-slate-300 bg-white"
                                    }`}
                                  >
                                    {duplicateTargetTypeId === item.id && (
                                      <span className="m-auto h-2 w-2 rounded-full bg-white" />
                                    )}
                                  </span>
                                </div>
                              </button>
                            ))}
                          </div>
                        </AccordionContent>
                      </AccordionItem>
                    ))}
                  </Accordion>
                </div>
              )}
            </div>
          </div>
          <DialogFooter>
            <div className="flex w-full items-center justify-between gap-3">
              <p className="text-sm text-slate-500">
                {selectedDuplicateTargetType
                  ? `Type sélectionné : ${selectedDuplicateTargetType.name}`
                  : "Veuillez sélectionner un type de projet."}
              </p>
              <div className="flex items-center gap-2">
                <Button type="button" variant="outline" onClick={() => setDuplicateSourceItem(null)} disabled={isDuplicating}>
                  Annuler
                </Button>
                <Button type="button" disabled={!duplicateTargetTypeId || isDuplicating} onClick={() => void handleConfirmDuplicate()}>
                  {isDuplicating ? "Duplication..." : "Dupliquer le formulaire"}
                </Button>
              </div>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ParametersFormsLibrary;
